/**
 * What PINE demands before a candidate can be set to a given status.
 *
 * Pure. No React, no network, no database - so it can be checked on its own,
 * which matters more here than anywhere else in the import.
 *
 * WHY THIS FILE CARRIES THE WEIGHT
 *
 * update_candidate_status_data enforces NONE of these rules. A candidate can be
 * set to Offer Accepted with no resume, no dossier and no compensation and it
 * will save. The only thing standing between a spreadsheet and a record a human
 * could never have created is this table being right.
 *
 * It is copied from view/candidate/edit_status.jsx:384-410, deliberately
 * verbatim rather than paraphrased. If that changes, change this. The two are
 * not generated from one source and nothing will tell you when they drift.
 *
 * Full taxonomy and what each status means: SAKETH-KB/MD/status_map.md.
 */

/* The table, exactly as edit_status.jsx expresses it. ------------------- */

const NEEDS_EVALUATED_BY = [28, 29, 30];
const NEEDS_DATE_TIME = [28, 29, 30, 43, 45, 54];
const NEEDS_RESUME = [27, 28, 29, 30, 31, 32, 33, 42, 43, 44, 45, 46, 47, 48, 49, 50, 54];
const NEEDS_DOSSIER = [33, 42, 43, 44, 45, 46, 47, 48, 49, 50, 54];
const NEEDS_BACKGROUND = [48];
const NEEDS_MEETING_WITH = [43, 45];
const NEEDS_MEETING_PLACE = [45];

// 33, and 42 through 54. Written as edit_status.jsx writes it rather than as a
// list, because the range is what that file says and a transcribed list is one
// more thing that can drift.
const needsCompensation = (id) => id === 33 || (id >= 42 && id <= 54);

// Offer Accepted opens a separate Contribution form before it will submit
// (edit_status.jsx:532). A modal inside a modal is not worth building for one
// rare status, so it is never finished here - it keeps its link out to PINE.
const HAS_ITS_OWN_WORKFLOW = [54];

/* --------------------------------------------------------------------- */

/**
 * @param {number} statusId  the status the SHEET wants, not the current one
 * @param {object} known
 *   @param {boolean} known.roleWantsDossier    role.dossier === 1
 *   @param {boolean} known.candidateHasResume  a file already on the candidate,
 *                                              from any role
 *   @param {boolean} known.candidateHasComp    compensation against their
 *                                              latest current employer
 *
 * @returns {{ fields: string[], blocked: string|null }}
 *   fields  what to ask this person for, in the order the screen shows sections
 *   blocked when set, this person cannot be finished here at all and the string
 *           says why. `fields` is empty in that case - asking for a file we
 *           cannot use would waste the user's time and upload a document
 *           against a status that will never be set.
 */
export function requirementsFor(statusId, known = {}) {
  const id = Number(statusId);

  const {
    roleWantsDossier = false,
    candidateHasResume = false,
    candidateHasComp = false,
  } = known;

  if (!Number.isInteger(id)) {
    return {
      fields: [],
      blocked: "No status came from the sheet.",
    };
  }

  if (HAS_ITS_OWN_WORKFLOW.includes(id)) {
    return {
      fields: [],
      blocked: "Needs the contribution form, on the candidate's page in PINE.",
    };
  }

  // Compensation is not a field anyone can type here. It attaches to the
  // candidate's current employer, and a freshly imported candidate has no
  // employment record until the LinkedIn sync runs - so there is nothing to
  // attach it to and no screen can fix that. See SPEC.MD 4.2.1.
  if (needsCompensation(id) && !candidateHasComp) {
    return {
      fields: [],
      blocked:
        "Needs current compensation. No employer on file yet — the sync " +
        "adds it in about 20 minutes.",
    };
  }

  const fields = [];

  // Files first, matching the order the screen shows its sections.
  //
  // The resume is required ONLY if the candidate has none already. Resumes live
  // in candidate_has_files keyed by candidate, not by role, so one uploaded for
  // an earlier search counts - edit_status.jsx:824 applies the same rule, and
  // being stricter would put people on a list who never needed to be there.
  if (NEEDS_RESUME.includes(id) && !candidateHasResume) fields.push("resume");

  // The dossier is demanded only when the role asks for one.
  if (NEEDS_DOSSIER.includes(id) && roleWantsDossier) fields.push("dossier");

  if (NEEDS_BACKGROUND.includes(id)) fields.push("background");

  // Then the typed fields.
  if (NEEDS_DATE_TIME.includes(id)) fields.push("dateTime");
  if (NEEDS_EVALUATED_BY.includes(id)) fields.push("evaluatedBy");
  if (NEEDS_MEETING_WITH.includes(id)) fields.push("meetingWith");
  if (NEEDS_MEETING_PLACE.includes(id)) fields.push("meetingPlace");

  return { fields, blocked: null };
}

/**
 * Has everything this person needs been supplied?
 *
 * The screen writes PEOPLE, not fields - a half-filled person is not written at
 * all, so this is what the save button counts.
 */
export function isComplete(requirements, filled = {}) {
  if (requirements.blocked) return false;

  return requirements.fields.every((field) => {
    const value = filled[field];
    if (value === undefined || value === null || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

/**
 * The sections the screen draws, in order, and what each one is called.
 *
 * Here rather than in the component so that adding a field means touching the
 * table and this list, and nothing else.
 */
export const FIELD_SECTIONS = [
  { key: "resume", label: "CV", kind: "file", name: "userfile" },
  { key: "dossier", label: "Dossier", kind: "file", name: "userfile1" },
  { key: "background", label: "Background check", kind: "file", name: "userfile2" },
  { key: "dateTime", label: "Date and time", kind: "datetime" },
  { key: "evaluatedBy", label: "Evaluated by", kind: "people" },
  { key: "meetingWith", label: "Meeting with", kind: "interviewers" },
  { key: "meetingPlace", label: "Meeting place", kind: "text" },
];
